/**
 * @file agentService.js
 * @description Fleet Agent API — the agent asks for a machine update inside the
 *              app (chat message), and escalates by ringing you here with an
 *              internet call. These endpoints back the AgentCallCenter.
 */
import apiClient from './apiClient';

const agentService = {
    /** Calls ringing for the signed-in user right now (socket catch-up path). */
    async getActiveCalls() {
        return apiClient.get('/agent/calls/active');
    },

    /** Answer a ringing call with what is happening on the machine. */
    async answerCall(callId, response) {
        return apiClient.post(`/agent/calls/${callId}/answer`, { response });
    },

    /** Decline a ringing call — the agent escalates to management instead. */
    async declineCall(callId) {
        return apiClient.post(`/agent/calls/${callId}/decline`);
    },

    /** The agent's open questions (status: 'open' | 'all'). */
    async getFollowups(status = 'open') {
        return apiClient.get(`/agent/followups?status=${encodeURIComponent(status)}`);
    },

    /** Answer a question without waiting for the call. */
    async respondFollowup(followupId, response) {
        return apiClient.post(`/agent/followups/${followupId}/respond`, { response });
    },

    /** Operations → Issues: every issue and who on the ladder it went to (status: 'open' | 'all'). */
    async getIssues(status = 'open') {
        return apiClient.get(`/agent/issues?status=${encodeURIComponent(status)}`);
    },

    /** The machines an issue can be raised on. */
    async getIssueMachines() {
        return apiClient.get('/agent/issues/machines');
    },

    /** Raise an issue by hand — it goes up the supervisor → manager → head ladder. */
    async raiseIssue({ equipment_id, description, severity }) {
        return apiClient.post('/agent/issues', { equipment_id, description, severity });
    },

    /** Close an issue with a note. */
    async resolveIssue(issueId, note) {
        return apiClient.post(`/agent/issues/${issueId}/resolve`, { note });
    },

    /** Who sits on each rung, plus the people and customers to pick from. */
    async getEscalationChain() {
        return apiClient.get('/agent/escalation-chain');
    },

    /** Replace the chain: [{ level, scope, user_id }] ('' scope = every customer). */
    async saveEscalationChain(chain) {
        return apiClient.put('/agent/escalation-chain', { chain });
    },

    /** Recent sweeps — "is the agent working?". */
    async getRuns(limit = 20) {
        return apiClient.get(`/agent/runs?limit=${limit}`);
    },
};

export default agentService;
